import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  icon?: ReactNode;
  suffix?: ReactNode;
}

/**
 * Labelled text input.
 *
 * The label wraps the caption and the bordered control, so clicking anywhere
 * in the 38px box -- icon, padding, the empty space beside a short value --
 * focuses the input. The hint and error sit OUTSIDE it and are wired through
 * aria-describedby.
 *
 * Wrapping all four in one <label>, as this did, makes the input's accessible
 * NAME the label plus the hint plus the error: a field announced as "Password"
 * was really "Password Leave blank to keep the current password", and an
 * invalid one grew its error message into its own name. A description is the
 * right channel for both -- announced after the name, separately, and
 * silenceable by verbosity settings the way a name is not. Dropping the label
 * element altogether fixes that but costs the whole control's click target,
 * which is a worse trade on touch.
 */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, icon, suffix, className, id, ...props },
  ref,
) {
  const auto = useId();
  const inputId = id ?? `field-${auto}`;
  const messageId = `${inputId}-message`;
  // `||`, not `??`, to match the render branch below exactly: that tests
  // truthiness, so an empty-string error falls through to the hint there and
  // must do the same here, or aria-describedby would point at a span that
  // never rendered.
  const message = error || hint;
  return (
    <div className={cn('block', className)}>
      <label htmlFor={inputId} className="block">
        {label && <span className="mb-1.5 block font-ui text-xs font-semibold text-fg-dim">{label}</span>}
        <span
          className={cn(
            'flex h-[38px] items-center gap-2.5 rounded-md border bg-surface px-3 focus-within:[box-shadow:var(--focus-ring)] has-[input:disabled]:bg-surface-2 has-[input:disabled]:opacity-70',
            error ? 'border-error-border focus-within:border-error-ink' : 'border-line-2 focus-within:border-brand',
          )}
        >
          {icon && (
            <span aria-hidden="true" className="grid place-items-center text-fg-mute">
              {icon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            aria-describedby={message ? messageId : undefined}
            // Colour alone marked the invalid state, which a colour-blind or
            // high-contrast user cannot see and a screen reader never hears.
            aria-invalid={error ? true : undefined}
            className="min-w-0 flex-1 bg-transparent font-ui text-sm text-fg outline-none placeholder:text-fg-mute disabled:cursor-not-allowed"
            {...props}
          />
          {/* Inside the label, so it would otherwise join the input's
              accessible name. A suffix is a visual adornment; a caller that
              needs it announced should say so in the hint. */}
          {suffix && (
            <span aria-hidden="true" className="whitespace-nowrap font-mono text-xs text-fg-mute">
              {suffix}
            </span>
          )}
        </span>
      </label>
      {error ? (
        <span id={messageId} className="mt-1.5 block text-[11.5px] text-error-ink">
          {error}
        </span>
      ) : (
        hint && (
          <span id={messageId} className="mt-1.5 block text-[11.5px] text-fg-mute">
            {hint}
          </span>
        )
      )}
    </div>
  );
});
